import type { CustomCartItem } from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { Button } from '@components/Button/Button';
import { QuickCartPrice } from '@components/Cart/CartPrice';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { CustomImage } from '@components/Media/Image/Image';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useRemoveItem } from '@hooks/cart/useRemoveItem';
import { log } from '@lib/logs';
import Link from 'next/link';
import { type ReactElement, useEffect, useState } from 'react';
import { Quantity } from '../Quantity';

export function QuickCartItem({
  item,
}: {
  item: CustomCartItem;
}): ReactElement {
  const removeItem = useRemoveItem({
    include: ['line_items.physical_items.options'],
  });
  const [updating, setUpdating] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [quantity, setQuantity] = useState(item.quantity);
  const [errorMessage, setErrorMessage] = useState(
    <Paragraph>Unable to remove that item</Paragraph>
  );

  const handleRemove = async () => {
    setUpdating(true);

    try {
      const res = await removeItem({ id: item.id });
      if (res.error) {
        setUpdating(false);
        setErrorMessage(<Paragraph>{res.error}</Paragraph>);
        setError(true);
      }
    } catch (error) {
      log({ error, location: 'QuickCartItem handleRemove' });
      setUpdating(false);
      setErrorMessage(<Paragraph>Unable to remove that item</Paragraph>);
      setError(true);
    } finally {
      // in case of error, set back to false so opacity resets
      setUpdating(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    // Reset the quantity state if the item quantity changes
    if (item.quantity !== Number(quantity)) {
      if (mounted) setQuantity(item.quantity);
    }

    return function cleanup() {
      mounted = false;
    };
  }, [item.quantity, quantity]);

  if (!item) return <div />;

  return (
    <div className={updating ? 'opacity-50' : ''}>
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
      <div className="grid grid-cols-[60px_auto_90px] gap-4 mx-1 my-1.5 items-center">
        <Link
          href={item.url}
          passHref
          prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
        >
          <CustomImage image={item.image} width={60} height={60} />
        </Link>

        {/* Title, Options, and Price */}
        <div>
          <TextLink href={item.url} target="_top" className="">
            {item.title}
          </TextLink>
          <div className="flex gap-1 leading-5 text-xs">
            {item.variant.options?.filter(
              (o) => o.title === 'Package Size'
            )[0] && (
              <span>
                {
                  item.variant.options?.filter(
                    (o) => o.title === 'Package Size'
                  )[0].value
                }
              </span>
            )}
            <span>
              {
                item.variant.options?.filter((o) => o.title === 'Flavour')[0]
                  ?.value
              }
            </span>
            {!item.variant.options
              ?.map((o) => o.title)
              .includes('Package Size') && (
              <span data-testid="variant-options">
                {item.variant.options?.map((o) => o.value).join(' ')}
              </span>
            )}
          </div>
          <QuickCartPrice
            retail={item.original_price * item.quantity}
            discounted={item.extended_sale_price as number}
          />
        </div>

        {/* Quantity and remove */}
        <div className="flex flex-col justify-center gap-1">
          <Quantity quantity={quantity} lineItem={item} />
          <Button
            type="button"
            aria-label="Remove item from cart"
            buttonStyle="text"
            onClick={handleRemove}
            className="gae-remove-item text-xs text-grey-40 no-underline"
            data-product-id={item.product_id}
            data-variant-id={item.variant.variantId}
            data-product-name={item.title}
            data-product-price={item.sale_price}
            data-product-quantity={item.quantity}
            disabled={updating}
          >
            Remove
          </Button>
        </div>
      </div>
      <HorizontalDivider className="m-0 via-grey-mid" />
    </div>
  );
}
